/** @typedef {import('headlight-configurable-application').Pict} Pict */

const PictSectionInputExtension = require('pict-section-form').PictInputExtensionProvider;

class CustomInputHandler extends PictSectionInputExtension
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;
	}

	getSelectInputHTMLID(pInputHTMLID)
	{
		return `#ENTITY-SELECT-MULTIPLE-FOR-${pInputHTMLID}`;
	}

	getTabularSelectInputID(pInputHTMLID, pRowIndex)
	{
		return `#ENTITY-SELECT-MULTIPLE-TABULAR-DATA-${pInputHTMLID}-${pRowIndex}`;
	}

	getTabularSelectDropdownID(pInputHTMLID, pRowIndex)
	{
		return `#ENTITY-SELECT-MULTIPLE-TABULAR-DROPDOWN-${pInputHTMLID}-${pRowIndex}`;
	}

	/**
	 * Temporary loading skeleton for the input element.
	 * @param {object} inputID The input id.
	 */
	addLoadingSkeleton(inputID, rawID)
	{
		return;
		if (!this.pict?.AppData?.Interactive || this.pict?.AppData?.DocumentData?.ReportData?.Locked)
		{
			return;
		}
		const tmpLoadingBox = document.createElement('div');
		tmpLoadingBox.id = `LOADING-${rawID}`;
		tmpLoadingBox.classList.add('input', 'is-skeleton');
		tmpLoadingBox.style.left = '0';
		tmpLoadingBox.style.position = 'absolute';
		tmpLoadingBox.style.opacity = '0.5';
		this.pict.ContentAssignment.getElement(inputID)[0]?.parentElement?.appendChild(tmpLoadingBox);
		this.pict.ContentAssignment.addClass(inputID, 'is-hidden');
	}

	/**
	 * Remove the loading skeleton from the input element.
	 * @param {object} inputID The input id.
	 */
	removeLoadingSkeleton(inputID, rawID)
	{
		return;
		const tmpLoadingBox = this.pict.ContentAssignment.getElement(`#LOADING-${rawID}`);
		if (tmpLoadingBox && tmpLoadingBox.length > 0)
		{
			tmpLoadingBox[0].remove();
		}
		this.pict.ContentAssignment.removeClass(inputID, 'is-hidden');
	}

	/**
	 * Determines if a word starts with a leading vowel.
	 * @param {string} pWord The word to check.
	 * @returns {boolean} True if the word starts with a vowel, false otherwise.
	 */
	hasLeadingVowel(pWord)
	{
		return [ 'A', 'E', 'I', 'O', 'U' ].includes(pWord.charAt(0).toUpperCase());
	}

	// TODO: we need to handle Project Joins for all entity selectors in a more generic way - this is just a temporary solution for until we can implement that
	localHandleSearchFilters(pFilters, pInput, pRowIndex)
	{
		pFilters = pFilters || [];

		// Project scoping for project-resident entities. Off-switch:
		// PictForm.disableProjectFilteringForEntity.
		const tmpProjectEntityList = [ 'LineItem', 'BidItem' ];
		if (!pInput.PictForm.disableProjectFilteringForEntity &&
			tmpProjectEntityList.includes(pInput.PictForm.Entity))
		{
			const tmpProjectID = this.pict.AppData?.DocumentData?.ReportData?.IDProject;
			if (tmpProjectID && !pFilters.includes(`FBV~IDProject~EQ~${tmpProjectID}`))
			{
				pFilters.push(`FBV~IDProject~EQ~${tmpProjectID}`);
			}
		}

		return pFilters;
	}

	/**
	 * Initialize the Select2 plugin for the input element.
	 * @param {object} pInput The input object.
	 */
	initializeSelect2(pInput, pRowIndex)
	{
		if (window['jQuery'] && window['jQuery'].fn.select2)
		{
			const tmpConfigOptions = {
				Input: pInput,
				Multiple: true,
				SearchFallbackPlaceholder: `Select ${ this.hasLeadingVowel(pInput.PictForm.Entity) ? 'an' : 'a' } ${ pInput.PictForm.Entity }...`,
				ApplySort: true,
				JoinEntity: pInput.PictForm.JoinEntity,
				JoinEntityFirst: pInput.PictForm.JoinEntityFirst ?? true,
				EntityDisplayField: pInput.PictForm.EntityDisplayField,
				JoinEntityDisplayField: pInput.PictForm.JoinEntityDisplayField,
				JoinField: pInput.PictForm.JoinField ? pInput.PictForm.JoinField : pInput.PictForm.JoinEntity ? `ID${ pInput.PictForm.JoinEntity }` : null,
				Disabled: pInput.PictForm.Disabled || false,
				EntityTagLast: pInput.PictForm.EntityTag && pInput.PictForm.EntityTagLast,
				HandleSearchFilters: (pFilters) => this.localHandleSearchFilters(pFilters, pInput, pRowIndex),
			};
			// Honor IdentityField so the bound value matches what the caller wants to persist
			// (e.g. storing Hash strings, not database IDs). Without this override the
			// select2 ajax pipeline always emits ID${Entity} as the option value.
			if (pInput.PictForm.IdentityField && pInput.PictForm.IdentityField !== `ID${pInput.PictForm.Entity}`)
			{
				const tmpIdentityField = pInput.PictForm.IdentityField;
				tmpConfigOptions.GetRecordID = (pEntity) => pEntity[tmpIdentityField];
			}
			// if an EntityTag is defined, use it to enhance the search and display
			if (pInput.PictForm.EntityTag)
			{
				tmpConfigOptions.GetRecordUserFacingID = (pEntity) => pEntity?.[pInput.PictForm.EntityTag] || 'N/A';
			}
			const tmpSelectOptions = this.pict.providers.Select2Util.getOptionsAjax(pInput.PictForm.Entity, tmpConfigOptions);
			// tabular vs. non-tabular context
			const methodNameByContext = (pRowIndex || pRowIndex === 0) ? 'getTabularSelectDropdownID' : 'getSelectInputHTMLID';
			// if the select2 plugin is not already initialized, initialize it now
			if (!window['jQuery'](this[methodNameByContext](pInput.Macro.RawHTMLID, pRowIndex)).data('select2'))
			{
				window['jQuery'](this[methodNameByContext](pInput.Macro.RawHTMLID, pRowIndex)).select2(tmpSelectOptions);
			}
		}
	}

	/**
	 * Initializes the input element for the Pict provider select input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The input value.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @return {boolean} - Returns true if the input element is successfully initialized, false otherwise.
	 */
	onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		this.addLoadingSkeleton(pInput);
		// initialize the select2 plugin
		return super.onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * Initializes a tabular input element.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The input value.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {number} pRowIndex - The index of the row.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @return {any} - The result of the initialization.
	 */
	onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		this.addLoadingSkeleton(this.getTabularSelectInputID(pInput.Macro.RawHTMLID, pRowIndex), pInput.Macro.RawHTMLID);
		return super.onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
	}

	/**
	 * Marshals data to the form for the given input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be marshaled.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @return {boolean}
	 */
	onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		//FIXME: base class doesn't allow this to be async - changed to run async safely for now, but not sure if this creates timing problems
		this._onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector).catch((pError) =>
		{
			this.pict.log.error(`Error in onDataMarshalToForm: ${pError}`);
		});
		return super.onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * Marshals data to the form for the given input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be marshaled.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {number} pRowIndex - The row index parameter.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @return {boolean}
	 */
	onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		//FIXME: base class doesn't allow this to be async - changed to run async safely for now, but not sure if this creates timing problems
		this._onDataMarshalToForm(pView, pGroup, null, pInput, pValue, pHTMLSelector, pRowIndex).catch((pError) =>
		{
			this.pict.log.error(`Error in onDataMarshalToForm: ${pError}`);
		});
		return super.onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
	}

	/**
	 * Marshals data to the form for the given input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be marshaled.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {number} [pRowIndex] - The row index parameter.
	 * @return {Promise<boolean>}
	 */
	async _onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pRowIndex)
	{
		const inputMethod = (pRowIndex || pRowIndex === 0) ? 'getTabularSelectDropdownID' : 'getSelectInputHTMLID';
		const tmpInputSelectElement = this.pict.ContentAssignment.getElement(this[inputMethod](pInput.Macro.RawHTMLID, pRowIndex))?.[0];
		if (!tmpInputSelectElement)
		{
			return false;
		}
		// if we have a value (ID), go fetch the Entity for display (Name, etc.)
		if (pValue)
		{
			const tmpValues = pValue && typeof(pValue) === 'string' ? pValue.split(',') : [];
			if (tmpInputSelectElement?.options?.length > 0)
			{
				// for each option that is not in the list, add it as a custom value
				for (let i = 0; i < tmpValues.length; i++)
				{
					let tmpValueSelected = false;
					for (let j = 0; j < tmpInputSelectElement.options.length; j++)
					{
						if (tmpInputSelectElement.options[j].value === tmpValues[i])
						{
							tmpInputSelectElement.options[j].selected = true;
							tmpValueSelected = true;
							break;
						}
					}
					if (!tmpValueSelected)
					{
						const tmpOption = document.createElement('option');
						tmpOption.value = tmpValues[i];
						const { Record: result, DisplayName: tmpDisplayName } = await this.pict.providers.Select2Util.getEntityWithDisplayAsync(pInput.PictForm.Entity, tmpValues[i],
						{
							Multiple: true,
							IdentityField: pInput.PictForm.IdentityField || null,
							JoinEntity: pInput.PictForm.JoinEntity,
							JoinEntityFirst: pInput.PictForm.JoinEntityFirst ?? true,
							JoinField: pInput.PictForm.JoinField ? pInput.PictForm.JoinField : pInput.PictForm.JoinEntity ? `ID${ pInput.PictForm.JoinEntity }` : null,
							EntityDisplayField: pInput.PictForm.EntityDisplayField,
							JoinEntityDisplayField: pInput.PictForm.JoinEntityDisplayField,
							EntityTagLast: pInput.PictForm.EntityTag && pInput.PictForm.EntityTagLast,
							HandleSearchFilters: (pFilters) => this.localHandleSearchFilters(pFilters, pInput),
						});
						// if we want to show the EntityTag in the selection display
						if (pInput.PictForm.EntityTag)
						{
							tmpOption.text = this.pict.providers.Select2Util.buildSelect2CompoundDisplayTemplate(
								result[pInput.PictForm.EntityTag] || 'N/A',
								tmpDisplayName,
								true,
								result.EntityTagLast,
							);
						}
						else
						{
							tmpOption.text = tmpDisplayName;
						}
						tmpOption.selected = true;
						tmpInputSelectElement.appendChild(tmpOption);
					}
				}
			}
			else
			{
				for (let i = 0; i < tmpValues.length; i++)
				{
					const tmpOption = document.createElement('option');
					tmpOption.value = tmpValues[i];
					const { Record: result, DisplayName: tmpDisplayName } = await this.pict.providers.Select2Util.getEntityWithDisplayAsync(pInput.PictForm.Entity, tmpValues[i],
					{
						IdentityField: pInput.PictForm.IdentityField || null,
						JoinEntity: pInput.PictForm.JoinEntity,
						JoinEntityFirst: pInput.PictForm.JoinEntityFirst ?? true,
						JoinField: pInput.PictForm.JoinField ? pInput.PictForm.JoinField : pInput.PictForm.JoinEntity ? `ID${ pInput.PictForm.JoinEntity }` : null,
						EntityDisplayField: pInput.PictForm.EntityDisplayField,
						JoinEntityDisplayField: pInput.PictForm.JoinEntityDisplayField,
						EntityTagLast: pInput.PictForm.EntityTag && pInput.PictForm.EntityTagLast,
						HandleSearchFilters: (pFilters) => this.localHandleSearchFilters(pFilters, pInput),
					});
					// if we want to show the EntityTag in the selection display
					if (pInput.PictForm.EntityTag)
					{
						tmpOption.text = this.pict.providers.Select2Util.buildSelect2CompoundDisplayTemplate(
							result[pInput.PictForm.EntityTag] || 'N/A',
							tmpDisplayName,
							true,
							result.EntityTagLast,
						);
					}
					else
					{
						tmpOption.text = tmpDisplayName;
					}
					tmpOption.selected = true;
					tmpInputSelectElement.appendChild(tmpOption);
				}
			}
		}
		this.removeLoadingSkeleton(pInput, pRowIndex);
		this.initializeSelect2(pInput, pRowIndex);
	}

	/**
	 * Handles the data request event for a select input in the PictProviderInputSelect class.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value object.
	 * @param {string} pHTMLSelector - The HTML selector object.
	 * @return {any} - The result of the onDataRequest method.
	 */
	onDataRequest(pView, pInput, pValue, pHTMLSelector)
	{
		const tmpInputSelectValue = this.pict.ContentAssignment.readContent(this.getSelectInputHTMLID(pInput.Macro.RawHTMLID));
		this.pict.ContentAssignment.assignContent(pHTMLSelector, tmpInputSelectValue);
		pView.dataChanged(pInput.Hash);
		return super.onDataRequest(pView, pInput, tmpInputSelectValue, pHTMLSelector);
	}

	/**
	 * Handles the data request event for a tabular input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value object.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {number} pRowIndex - The row index.
	 * @return {any} - The result of the data request.
	 */
	onDataRequestTabular(pView, pInput, pValue, pHTMLSelector, pRowIndex)
	{
		const tmpInputSelectValue = this.pict.ContentAssignment.readContent(this.getTabularSelectDropdownID(pInput.Macro.RawHTMLID, pRowIndex));
		this.pict.ContentAssignment.assignContent(this.getTabularSelectInputID(pInput.Macro.RawHTMLID, pRowIndex), tmpInputSelectValue);
		pView.dataChangedTabular(pInput.PictForm.GroupIndex, pInput.PictForm.InputIndex, pRowIndex);
		return super.onDataRequestTabular(pView, pInput, pValue, pHTMLSelector, pRowIndex);
	}

	/**
	 * Called when the data change function is called
	 *
	 * Called when an input has this Provider hash in its 'Providers' array.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The new value of the input object
	 * @param {string} pHTMLSelector - The HTML selector for the input object
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 */
	onDataChange(pView, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		// Special handling for filters inputs, do not remove.
		if (pView.Hash === 'PSRSFilterProxyView')
		{
			this.pict.manifest.setValueAtAddress(pView.Bundle, pInput.PictForm.InformaryDataAddress.replace('StringArrayValue', 'Values'), pValue ? pValue.split(',') : []);
		}
		// Plan scope from the picked sample (multi-select case).
		// The resolver is single-project; multi-sample means the project is
		// only well-defined when all picks share one. Best-effort: take the
		// first identifier and warn loudly when there's more than one so a
		// real misconfiguration surfaces in logs. Resolver dedupes when the
		// project hasn't changed.
		if (pInput.PictForm?.Entity === 'Sample' && pValue && this.pict.providers.SpecScopeResolver)
		{
			const tmpIdentifiers = typeof pValue === 'string' ? pValue.split(',').map((v) => v.trim()).filter((v) => v.length > 0) : [];
			if (tmpIdentifiers.length > 1)
			{
				this.pict.log.warn(`EntitySelectorMultiple [${pInput?.Hash}]: multi-sample selection with plan scope active — using the first sample (${tmpIdentifiers[0]}) as the scope source. Picker has ${tmpIdentifiers.length} samples selected.`);
			}
			if (tmpIdentifiers.length > 0)
			{
				const tmpIdentityField = pInput.PictForm.IdentityField || 'IDSample';
				this.pict.providers.SpecScopeResolver.loadForSampleByIdentifier?.(tmpIdentifiers[0], tmpIdentityField);
			}
		}
		return super.onDataChange(pView, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}
}

module.exports = CustomInputHandler;
